import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SectionLabel, Reveal } from "../components/common";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { IMG, ENQUIRY_TYPES, COMPANY } from "../mock";

const EMPTY = { fullName: "", email: "", organisation: "", type: "General Enquiry", message: "" };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Please add a message";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent", { description: "Thank you — we'll be in touch shortly." });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send message", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  const inputCls = (k) =>
    `w-full bg-transparent border-b py-3 text-white placeholder-neutral-600 outline-none transition-colors ${
      errors[k] ? "border-red-500" : "border-white/15 focus:border-[#c3e63c]"
    }`;

  return (
    <main>
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden bg-[#0a0a0a]">
        <img src={IMG.corpA} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]/30" />
        <div className="dot-grid absolute inset-0 opacity-25" />
        <div className="relative mx-auto w-full max-w-[1400px] px-5 md:px-10 pb-16 pt-32">
          <SectionLabel>A Garry Industries Ventures · Contact</SectionLabel>
          <h1 className="font-display mt-6 max-w-3xl text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight">
            Let's start a conversation.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-neutral-300">
            Whether you represent a railway, an OEM, an investor or an institution — we'd welcome the opportunity to talk.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="bg-[#0a0a0a] py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
            {/* Left info */}
            <div>
              <Reveal><SectionLabel>A Garry Industries Ventures · Get in touch</SectionLabel></Reveal>
              <Reveal><h2 className="font-display mt-6 text-3xl md:text-4xl font-bold tracking-tight">Let's talk.</h2></Reveal>
              <div className="mt-10 space-y-8">
                <Reveal className="flex items-start gap-4">
                  <span className="grid h-10 w-10 place-items-center border border-white/15 text-[#c3e63c] shrink-0"><Mail size={16} /></span>
                  <div>
                    <div className="eyebrow text-neutral-500">Email</div>
                    <a href={`mailto:${COMPANY.email}`} className="mt-1 block text-white hover:text-[#c3e63c] transition-colors">{COMPANY.email}</a>
                  </div>
                </Reveal>
                <Reveal delay={80} className="flex items-start gap-4">
                  <span className="grid h-10 w-10 place-items-center border border-white/15 text-[#c3e63c] shrink-0"><MapPin size={16} /></span>
                  <div>
                    <div className="eyebrow text-neutral-500">Location</div>
                    <div className="mt-1 text-white">{COMPANY.location}</div>
                  </div>
                </Reveal>
                <Reveal delay={140}>
                  <div className="mt-4 border-t border-white/10 pt-6">
                    <span className="inline-block bg-[#c3e63c] px-3 py-1.5 font-display font-bold uppercase tracking-[0.08em] text-[#0a0a0a] text-xs md:text-sm">Its a Garry Industries venture for railways projects!</span>
                  </div>
                </Reveal>
              </div>
            </div>

            {/* Form */}
            <Reveal delay={100} className="border border-white/10 bg-[#0d0d0d] p-7 md:p-10">
              <form onSubmit={submit} className="space-y-7" noValidate>
                <div>
                  <label className="eyebrow text-neutral-400">Full Name *</label>
                  <input className={inputCls("fullName")} value={form.fullName} onChange={(e) => set("fullName", e.target.value)} placeholder="Your name" />
                  {errors.fullName && <p className="mt-1.5 text-xs text-red-400">{errors.fullName}</p>}
                </div>
                <div className="grid gap-7 sm:grid-cols-2">
                  <div>
                    <label className="eyebrow text-neutral-400">Email *</label>
                    <input className={inputCls("email")} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" />
                    {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="eyebrow text-neutral-400">Organisation</label>
                    <input className={inputCls("organisation")} value={form.organisation} onChange={(e) => set("organisation", e.target.value)} placeholder="Company / institution" />
                  </div>
                </div>
                <div>
                  <label className="eyebrow text-neutral-400 block mb-3">I am reaching out as</label>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="w-full bg-transparent border-white/15 text-white rounded-none h-12 focus:ring-[#c3e63c]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d0d0d] border-white/15 text-white">
                      {ENQUIRY_TYPES.map((t) => (
                        <SelectItem key={t} value={t} className="focus:bg-white/10 focus:text-[#c3e63c]">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="eyebrow text-neutral-400">Message *</label>
                  <textarea rows={4} className={inputCls("message") + " resize-none"} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your enquiry" />
                  {errors.message && <p className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
                </div>
                <button type="submit" disabled={sending} className="btn-accent inline-flex items-center gap-2.5 bg-[#c3e63c] px-7 py-3.5 eyebrow text-[#0a0a0a] hover:bg-[#d4f24d] disabled:opacity-70">
                  {sending ? <><Loader2 size={15} className="animate-spin" /> Sending</> : <>Send Message <ArrowRight size={15} /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
